<template>
  <div
    class="email-wrapper"
    tabindex="0"
    @keydown.down.prevent="onMoveHover('down')"
    @keydown.up.prevent="onMoveHover('up')"
    @keydown.enter.prevent="onEnter"
  >
    <BaseInput
      v-model="email"
      type="email"
      class="email-wrapper__input"
      autocomplete="email"
      autofill="email"
      :maxlength="64"
      :placeholder="c_placeholder"
      :class="{ 'error': error }"
      v-bind="$attrs"
      v-on="listeners"
      @input="onInputEmail"
      @blur="onBlur"
    />
    <Transition name="fade" mode="out-in">
      <ul v-if="showDomainSuggestions" class="email-wrapper__domain-list">
        <li
          v-for="(domain, index) in filteredDomains"
          :key="index"
          class="email-wrapper__domain-item"
          :class="{ 'hover': index === hoverEmailIndex }"
          @click="selectDomain(domain)"
        >
          <UiText
            font="body-text-small"
            color="neutral-b10"
            :text="emailPrefix"
          />
          <UiText
            font="body-text-small"
            color="primary-700"
            :text="`@${domain}`"
          />
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'

/** components */
import BaseInput from '@/components/Base/Input'

export default {
  name: 'BaseInputEmail',
  components: {
    BaseInput,
  },
  mixins: [validationMixin],
  props: {
    error: { type: Boolean },
    placeholder: { type: String },
  },
  validations() {
    return {
      email: {
        required,
        email,
      },
    }
  },
  data() {
    return {
      email: '',
      showDomainSuggestions: false,
      filteredDomains: [],
      hoverEmailIndex: -1,
    }
  },
  computed: {
    listeners() {
      const { input, ...listeners } = this.$listeners
      return listeners
    },
    emailPrefix() {
      const atIndex = this.email.indexOf('@')
      return atIndex === -1 ? this.email : this.email.slice(0, atIndex)
    },
    c_placeholder() {
      return this.placeholder || this.$t('regAuthForgot.placeholder.email')
    },
  },
  watch: {
    '$attrs.value': {
      immediate: true,
      handler(v) {
        this.email = v
      },
    },
  },
  static: {
    domains: [
      'ukr.net',
      'gmail.com',
      'icloud.com',
      'bigmir.net',
      'i.ua',
      'inbox.lv',
      'live.com',
      'mail.i.ua',
      'meta.ua',
      'op.pl',
      'outlook.com',
      'proton.me',
      'wp.pl',
    ],
    stopDomains: [
      'hi2.in',
      'temp-mail.org',
      'brinkc.com',
      '10minutemail.net',
    ],
    numberOfSuggestions: 4,
  },
  methods: {
    onInputEmail() {
      this.$emit('input', this.email)
      const atIndex = this.email.indexOf('@')
      if (atIndex === -1) return this.showDomainSuggestions = false

      const userTypedDomain = this.email.slice(atIndex + 1)
      const exactMatch = this.$options.static.domains.some(
        domain => domain.toLowerCase() === userTypedDomain
      )
      if (exactMatch) return this.showDomainSuggestions = false

      this.showDomainSuggestions = true
      this.filteredDomains = this.$options.static.domains
        .filter(domain => domain.toLowerCase().includes(userTypedDomain))
        .slice(0, this.$options.static.numberOfSuggestions)
      this.showDomainSuggestions = this.filteredDomains.length > 0
      this.hoverEmailIndex = -1
    },
    onEnter() {
      if (this.showDomainSuggestions && this.hoverIndex !== -1) {
        this.selectHoverDomain()
        this.onInputEmail()
      }
      this.showDomainSuggestions = false
    },
    onMoveHover(direction) {
      if (direction === 'down' && this.hoverEmailIndex < this.filteredDomains.length - 1) {
        this.hoverEmailIndex++
      }
      else if (direction === 'up' && this.hoverEmailIndex > 0) {
        this.hoverEmailIndex--
      }
    },
    selectHoverDomain() {
      if (this.hoverEmailIndex !== -1) {
        this.selectDomain(this.filteredDomains[this.hoverEmailIndex])
      }
    },
    selectDomain(domain) {
      const atIndex = this.email.indexOf('@')
      if (atIndex !== -1) {
        this.email = this.email.slice(0, atIndex + 1) + domain
      }
      this.showDomainSuggestions = false
      this.onInputEmail()
    },
    onBlur() {
      if (this.showDomainSuggestions) {
        this.showDomainSuggestions = false
      }
    },
  },
}
</script>

<style lang="scss" scoped src="./style.scss" />
